"use client";

import clsx from "clsx";
import Link from "next/link";
import { useTranslation } from "react-i18next";
import Logo from "@/components/Logo";
import { RedesignButton } from "@/components/redesign/RedesignButton";
import { getLocaleFromPathname, useLocalePath } from "@/hooks/useLocalePath";
import { useRoutedGailAppUrl } from "@/providers/AppLinkRoutingProvider";
import { pathMatchesEnglishRoute } from "@/utils/routes";
import { getDemoPageKey, withDemoSource } from "@/utils/demoSource";
import { useTrackDemoCtaClick } from "@/hooks/useDemoCtaTracking";

type RedesignNavLinkProps = {
  href: string;
  active?: boolean;
  children: React.ReactNode;
};

function RedesignNavLink({
  href,
  active,
  children,
}: Readonly<RedesignNavLinkProps>) {
  return (
    <Link
      href={href}
      className={clsx(
        "text-sm font-medium transition-colors",
        active ? "text-gail-primary" : "text-black/80 hover:text-black",
      )}
    >
      {children}
    </Link>
  );
}

type MarketingHeaderDesktopProps = {
  pathname: string | null;
};

export function MarketingHeaderDesktop({
  pathname,
}: Readonly<MarketingHeaderDesktopProps>) {
  const { t } = useTranslation();
  const lp = useLocalePath();
  const lang = getLocaleFromPathname(pathname);
  const trackDemoCtaClick = useTrackDemoCtaClick();
  const demoSource = `nav_${getDemoPageKey(pathname)}`;
  const dashboardHref = useRoutedGailAppUrl(
    process.env.NEXT_PUBLIC_GAIL_DASHBOARD_URL ?? "#",
  );

  return (
    <div className="hidden md:grid md:w-full md:grid-cols-3 md:items-center">
      <Link
        href={lp("/")}
        className="shrink-0 justify-self-start"
        aria-label={t("breadcrumb.home")}
      >
        <Logo className="h-[22px] md:h-6" />
      </Link>

      <nav
        aria-label="Primary"
        className="flex items-center justify-center gap-8 justify-self-center"
      >
        <RedesignNavLink
          href={lp("/product")}
          active={pathMatchesEnglishRoute(pathname, lang, "/product")}
        >
          {t("header.product")}
        </RedesignNavLink>
        <RedesignNavLink
          href={lp("/pricing")}
          active={pathMatchesEnglishRoute(pathname, lang, "/pricing")}
        >
          {t("header.pricing")}
        </RedesignNavLink>
        <RedesignNavLink
          href={lp("/compliance")}
          active={pathMatchesEnglishRoute(pathname, lang, "/compliance")}
        >
          {t("header.compliance")}
        </RedesignNavLink>
      </nav>

      <div className="flex items-center justify-end gap-6 justify-self-end">
        <RedesignNavLink href={dashboardHref}>
          {t("header.signIn")}
        </RedesignNavLink>
        <RedesignButton
          href={withDemoSource(lp("/contact-sales"), demoSource)}
          onClick={() =>
            trackDemoCtaClick({ source: demoSource, placement: "nav" })
          }
        >
          {t("redesign.hero.ctaLabel")}
        </RedesignButton>
      </div>
    </div>
  );
}
